import React from "react";

import { Pencil, Trash2 } from "lucide-react";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/src/components/ui/avatar";
import { getNameInitials } from "@/src/utils/initials";

import DataTableColumnHeader from "../../dataTable/DataTableColumnHeader";
import {
  actionsColumn,
  enabledStatusOptions,
  statusColumn,
} from "../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../dataTable/DataTableLinkCell";
import { ADMIN_USERS_ROUTE } from "../../routes/routes";

import type { TFunction } from "i18next";

import type AdminUser from "../../../models/AdminUser";
import type {
  ColumnLayout,
  DataTableColumnDef,
} from "../../dataTable/dataTable.types";

interface AdminUserColumnsOptions {
  t: TFunction;
  loggedInEmail: string;
  onEdit: (user: AdminUser) => void;
  onDelete: (user: AdminUser) => void;
}

export function getAdminUserHref(uuid: string): string {
  return `${ADMIN_USERS_ROUTE}/${uuid}`;
}

// Un propriétaire ne s'édite que par lui-même et ne se supprime pas.
export function canEditAdminUser(
  user: AdminUser,
  loggedInEmail: string,
): boolean {
  return !user.isOwner || user.email === loggedInEmail;
}

const AVATAR_LAYOUT: ColumnLayout = {
  width: "8%",
  minWidth: 70,
  align: "center",
};

function sortableTextColumn(
  t: TFunction,
  key: "lastName" | "firstName" | "email",
): DataTableColumnDef<AdminUser> {
  return {
    accessorKey: key,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t(key)} />
    ),
    meta: { searchable: true, label: t(key) },
  };
}

export function adminUserColumns({
  t,
  loggedInEmail,
  onEdit,
  onDelete,
}: AdminUserColumnsOptions): DataTableColumnDef<AdminUser>[] {
  return [
    statusColumn<AdminUser>("status", t("status"), enabledStatusOptions(t)),
    {
      id: "avatar",
      accessorFn: (user) => user.avatarUrl,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("avatar")} />
      ),
      enableSorting: false,
      meta: { ...AVATAR_LAYOUT, label: t("avatar") },
      cell: ({ row }) => (
        <Avatar className="inline-flex align-middle" size="sm">
          <AvatarImage alt="" src={row.original.avatarUrl} />
          <AvatarFallback>
            {getNameInitials(row.original.firstName, row.original.lastName)}
          </AvatarFallback>
        </Avatar>
      ),
    },
    {
      ...sortableTextColumn(t, "lastName"),
      cell: ({ row }) => (
        <DataTableLinkCell
          href={
            canEditAdminUser(row.original, loggedInEmail)
              ? getAdminUserHref(row.original.uuid)
              : undefined
          }
        >
          {row.original.lastName}
        </DataTableLinkCell>
      ),
    },
    sortableTextColumn(t, "firstName"),
    sortableTextColumn(t, "email"),
    {
      id: "company",
      accessorFn: (user) => user.company ?? "",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("company")} />
      ),
      meta: { searchable: true, label: t("company") },
    },
    {
      id: "roles",
      accessorFn: (user) => user.rolesString,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("roles")} />
      ),
      meta: { searchable: true, label: t("roles") },
      cell: ({ getValue }) => getValue<string>(),
    },
    actionsColumn<AdminUser>(
      (user) => {
        const editAction = {
          label: t("edit"),
          icon: <Pencil />,
          onClick: () => onEdit(user),
        };
        if (user.isOwner) {
          return canEditAdminUser(user, loggedInEmail) ? [editAction] : [];
        }
        return [
          editAction,
          {
            label: t("delete"),
            icon: <Trash2 />,
            variant: "destructive",
            onClick: () => onDelete(user),
            confirm: {
              title: t("delete"),
              description: t("deleteBody", { email: user.email }),
              confirmLabel: t("confirm"),
            },
          },
        ];
      },
      (user) => user.email,
    ),
  ];
}
